// =============================================================================
// PEDRA — src/lib/share.ts
// Compartilhamento do projeto pela URL, sem servidor e sem biblioteca externa.
//
//   Project  ->  "packed" (tuplas posicionais + dicionario de textos)
//            ->  JSON compacto  ->  UTF-8  ->  base64url  ->  "#p=<token>"
//
// O payload vai no FRAGMENTO (`#`), nunca na query: o fragmento nao e enviado
// ao servidor, entao a lista de pecas do cliente nao aparece em log de acesso.
//
// A "compactacao propria" e o formato packed: nomes de peca, materiais e cores
// se repetem muito, e o dicionario grava cada texto UMA vez. Junto com a
// truncagem de cauda das tuplas, isso corta ~60% em relacao ao JSON cru.
// =============================================================================

import type {
  CutOrientation,
  DimensionOrder,
  EdgeBandType,
  OptimizationPriority,
  Part,
  Project,
  Settings,
  StackMode,
  StockSheet,
  Unit,
} from '../core/types';
import { PROJECT_VERSION } from '../core/types';
import { colorForIndex, createDefaultSettings, newId } from '../core/defaults';
import { sanitizeProject } from './projectFile';
import { formatCount } from './format';

// -----------------------------------------------------------------------------
// CONSTANTES
// -----------------------------------------------------------------------------

/** Acima disso o link comeca a ser cortado por WhatsApp, e-mail e Excel. */
export const MAX_SHARE_LENGTH = 8000;

/** Primeiro caractere do token: identifica a versao do envelope. */
export const SHARE_TAG = 'A';

/** Versao da estrutura "packed". */
export const SHARE_PACK_VERSION = 1;

/** Nome do parametro dentro do hash. */
export const SHARE_PARAM = 'p';

/** Mensagem exibida quando o projeto nao cabe num link. */
export const SHARE_TOO_LARGE_MESSAGE =
  'Este projeto é grande demais para caber num link. Use "Exportar JSON" e envie o arquivo.';

/** Mensagem exibida quando o link chegou cortado, colado pela metade ou alterado. */
export const SHARE_BROKEN_LINK_MESSAGE =
  'Não foi possível abrir o projeto compartilhado: o link está incompleto ou corrompido. ' +
  'Peça o link novamente (ou o arquivo .pedra.json). O app continua funcionando normalmente.';

/**
 * Evento disparado em `window` quando um link compartilhado nao pode ser lido.
 * A interface pode escutar e chamar `preventDefault()` para exibir o aviso do
 * seu jeito; sem ninguem escutando, `share.ts` mostra a faixa de aviso.
 */
export const SHARE_ERROR_EVENT = 'pedra:share-error';

const UNIT_ORDER: readonly Unit[] = ['mm', 'cm', 'm', 'in', 'ft'];
const DIMENSION_ORDER_ORDER: readonly DimensionOrder[] = ['lengthFirst', 'widthFirst'];
const PRIORITY_ORDER: readonly OptimizationPriority[] = [
  'leastWaste',
  'leastCuts',
  'smallerSheets',
];
const CUT_ORIENTATION_ORDER: readonly CutOrientation[] = ['optimal', 'preferWidth', 'preferLength'];
const STACK_ORDER: readonly StackMode[] = ['auto', 'never', 'always'];

const FLAG_GRAIN = 1 << 0;
const FLAG_MATERIAL = 1 << 1;
const FLAG_EDGE_BANDING = 1 << 2;
const FLAG_LABELS = 1 << 3;
const FLAG_SINGLE_STOCK = 1 << 4;

// -----------------------------------------------------------------------------
// BASE64URL — implementacao propria (funciona em browser, worker e Node)
// -----------------------------------------------------------------------------

const B64_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

const B64_LOOKUP: Record<number, number> = (() => {
  const map: Record<number, number> = {};
  for (let i = 0; i < B64_ALPHABET.length; i++) map[B64_ALPHABET.charCodeAt(i)] = i;
  return map;
})();

/** Bytes -> base64url, sem `=` de preenchimento. */
export function toBase64Url(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const b0 = bytes[i] as number;
    const has1 = i + 1 < bytes.length;
    const has2 = i + 2 < bytes.length;
    const b1 = has1 ? (bytes[i + 1] as number) : 0;
    const b2 = has2 ? (bytes[i + 2] as number) : 0;

    out += B64_ALPHABET[b0 >> 2];
    out += B64_ALPHABET[((b0 & 0x03) << 4) | (b1 >> 4)];
    if (!has1) break;
    out += B64_ALPHABET[((b1 & 0x0f) << 2) | (b2 >> 6)];
    if (!has2) break;
    out += B64_ALPHABET[b2 & 0x3f];
  }
  return out;
}

/** base64url -> bytes. `null` quando aparece caractere fora do alfabeto. */
export function fromBase64Url(text: string): Uint8Array | null {
  const bytes: number[] = [];
  let buffer = 0;
  let bits = 0;
  for (let i = 0; i < text.length; i++) {
    const v = B64_LOOKUP[text.charCodeAt(i)];
    if (v === undefined) return null;
    buffer = (buffer << 6) | v;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      bytes.push((buffer >> bits) & 0xff);
    }
  }
  return new Uint8Array(bytes);
}

// -----------------------------------------------------------------------------
// DICIONARIO DE TEXTOS
// -----------------------------------------------------------------------------

interface Dictionary {
  list: string[];
  intern(value: string): number;
}

function createDictionary(): Dictionary {
  const index = new Map<string, number>();
  const list: string[] = [];
  return {
    list,
    intern(value: string): number {
      if (value === '') return -1;
      const hit = index.get(value);
      if (hit !== undefined) return hit;
      const next = list.length;
      list.push(value);
      index.set(value, next);
      return next;
    },
  };
}

function readDict(list: readonly unknown[], index: unknown): string {
  if (typeof index !== 'number' || index < 0 || index >= list.length) return '';
  const value = list[index];
  return typeof value === 'string' ? value : '';
}

// -----------------------------------------------------------------------------
// EMPACOTAMENTO
// -----------------------------------------------------------------------------

/** Remove do fim da tupla tudo que ja e o valor padrao. */
function trimTail(values: number[], defaults: readonly number[]): number[] {
  const out = values.slice();
  while (out.length > 0 && out[out.length - 1] === defaults[out.length - 1]) out.pop();
  return out;
}

/** Le a posicao `i` da tupla, caindo no padrao quando ela foi truncada. */
function at(values: readonly unknown[], i: number, fallback: number): number {
  const v = values[i];
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

const PART_DEFAULTS: readonly number[] = [0, 0, 1, -1, -1, 0, 1, -1, -1, -1, -1, -1];
const STOCK_DEFAULTS: readonly number[] = [0, 0, -1, -1, -1, 0, 1];

function packSettings(s: Settings): number[] {
  let flags = 0;
  if (s.considerGrain) flags |= FLAG_GRAIN;
  if (s.considerMaterial) flags |= FLAG_MATERIAL;
  if (s.edgeBandingEnabled) flags |= FLAG_EDGE_BANDING;
  if (s.labelsOnPanels) flags |= FLAG_LABELS;
  if (s.useSingleStockUnit) flags |= FLAG_SINGLE_STOCK;

  return [
    Math.max(0, UNIT_ORDER.indexOf(s.unit)),
    Math.max(0, DIMENSION_ORDER_ORDER.indexOf(s.dimensionOrder)),
    s.decimalPlaces,
    s.kerf,
    s.minTrimDimension,
    flags,
    Math.max(0, PRIORITY_ORDER.indexOf(s.priority)),
    Math.max(0, CUT_ORIENTATION_ORDER.indexOf(s.cutOrientation)),
    Math.max(0, STACK_ORDER.indexOf(s.stackSamePanels)),
    s.timeBudgetMs,
    s.seed,
  ];
}

function unpackSettings(raw: unknown): Settings {
  const base = createDefaultSettings();
  if (!Array.isArray(raw)) return base;
  const pick = <T>(order: readonly T[], i: number, fallback: T): T => {
    const idx = at(raw, i, -1);
    return idx >= 0 && idx < order.length ? (order[idx] as T) : fallback;
  };
  const flags = at(raw, 5, 0);
  return {
    unit: pick(UNIT_ORDER, 0, base.unit),
    dimensionOrder: pick(DIMENSION_ORDER_ORDER, 1, base.dimensionOrder),
    decimalPlaces: at(raw, 2, base.decimalPlaces),
    kerf: at(raw, 3, base.kerf),
    minTrimDimension: at(raw, 4, base.minTrimDimension),
    considerGrain: (flags & FLAG_GRAIN) !== 0,
    considerMaterial: (flags & FLAG_MATERIAL) !== 0,
    edgeBandingEnabled: (flags & FLAG_EDGE_BANDING) !== 0,
    labelsOnPanels: (flags & FLAG_LABELS) !== 0,
    useSingleStockUnit: (flags & FLAG_SINGLE_STOCK) !== 0,
    priority: pick(PRIORITY_ORDER, 6, base.priority),
    cutOrientation: pick(CUT_ORIENTATION_ORDER, 7, base.cutOrientation),
    stackSamePanels: pick(STACK_ORDER, 8, base.stackSamePanels),
    timeBudgetMs: at(raw, 9, base.timeBudgetMs),
    seed: at(raw, 10, base.seed),
  };
}

/**
 * Estrutura enxuta do projeto:
 * `[versao, nome, dicionario, settings, fitas, pecas, chapas]`.
 */
export type PackedProject = [
  number,
  string,
  string[],
  number[],
  number[][],
  number[][],
  number[][],
];

/** `Project` -> tuplas posicionais. Nao inclui resultado nem ids locais. */
export function packProject(p: Project): PackedProject {
  const dict = createDictionary();

  const bands: number[][] = p.edgeBands.map((b) => [
    dict.intern(b.name),
    b.thickness,
    dict.intern(b.color),
  ]);

  const bandIndex = new Map<string, number>();
  p.edgeBands.forEach((b, i) => bandIndex.set(b.id, i));
  const bandRef = (id: string | null): number =>
    id !== null && bandIndex.has(id) ? (bandIndex.get(id) as number) : -1;

  const parts: number[][] = p.parts.map((part) =>
    trimTail(
      [
        part.length,
        part.width,
        part.qty,
        dict.intern(part.label),
        dict.intern(part.material),
        part.orientation,
        part.enabled ? 1 : 0,
        dict.intern(part.color),
        bandRef(part.edgeBanding.top),
        bandRef(part.edgeBanding.right),
        bandRef(part.edgeBanding.bottom),
        bandRef(part.edgeBanding.left),
      ],
      PART_DEFAULTS,
    ),
  );

  const stock: number[][] = p.stock.map((s) =>
    trimTail(
      [
        s.length,
        s.width,
        s.qty === null ? -1 : s.qty,
        dict.intern(s.label),
        dict.intern(s.material),
        s.orientation,
        s.enabled ? 1 : 0,
      ],
      STOCK_DEFAULTS,
    ),
  );

  return [SHARE_PACK_VERSION, p.name, dict.list, packSettings(p.settings), bands, parts, stock];
}

/** Tuplas posicionais -> `Project` validado. `null` se a estrutura nao bate. */
export function unpackProject(raw: unknown): Project | null {
  if (!Array.isArray(raw)) return null;
  if (raw[0] !== SHARE_PACK_VERSION) return null;

  const name = typeof raw[1] === 'string' ? raw[1] : '';
  const dict: unknown[] = Array.isArray(raw[2]) ? raw[2] : [];
  const settings = unpackSettings(raw[3]);
  const rawBands: unknown[] = Array.isArray(raw[4]) ? raw[4] : [];
  const rawParts: unknown[] = Array.isArray(raw[5]) ? raw[5] : [];
  const rawStock: unknown[] = Array.isArray(raw[6]) ? raw[6] : [];

  const edgeBands: EdgeBandType[] = [];
  for (const entry of rawBands) {
    if (!Array.isArray(entry)) continue;
    edgeBands.push({
      id: newId('eb'),
      name: readDict(dict, entry[0]),
      thickness: at(entry, 1, 1_000),
      color: readDict(dict, entry[2]) || '#8C8C8C',
    });
  }
  const bandId = (i: number): string | null =>
    i >= 0 && i < edgeBands.length ? (edgeBands[i] as EdgeBandType).id : null;

  const parts: Part[] = [];
  for (const entry of rawParts) {
    if (!Array.isArray(entry)) continue;
    const index = parts.length;
    parts.push({
      id: newId('p'),
      length: at(entry, 0, 0),
      width: at(entry, 1, 0),
      qty: at(entry, 2, 1),
      label: readDict(dict, entry[3]),
      material: readDict(dict, entry[4]),
      orientation: ((): 0 | 1 | 2 => {
        const g = at(entry, 5, 0);
        return g === 1 || g === 2 ? g : 0;
      })(),
      enabled: at(entry, 6, 1) !== 0,
      edgeBanding: {
        top: bandId(at(entry, 8, -1)),
        right: bandId(at(entry, 9, -1)),
        bottom: bandId(at(entry, 10, -1)),
        left: bandId(at(entry, 11, -1)),
      },
      color: readDict(dict, entry[7]) || colorForIndex(index),
    });
  }

  const stock: StockSheet[] = [];
  for (const entry of rawStock) {
    if (!Array.isArray(entry)) continue;
    const qty = at(entry, 2, -1);
    stock.push({
      id: newId('s'),
      length: at(entry, 0, 0),
      width: at(entry, 1, 0),
      qty: qty < 0 ? null : qty,
      label: readDict(dict, entry[3]),
      material: readDict(dict, entry[4]),
      orientation: ((): 0 | 1 | 2 => {
        const g = at(entry, 5, 0);
        return g === 1 || g === 2 ? g : 0;
      })(),
      enabled: at(entry, 6, 1) !== 0,
    });
  }

  // A validacao defensiva final e a mesma da importacao de arquivo: um link
  // adulterado nao pode injetar qty gigante nem medida absurda.
  return sanitizeProject({
    version: PROJECT_VERSION,
    name,
    settings,
    edgeBands,
    parts,
    stock,
  });
}

// -----------------------------------------------------------------------------
// TOKEN
// -----------------------------------------------------------------------------

/**
 * Projeto -> token do link. Devolve `''` quando o resultado passa de
 * `MAX_SHARE_LENGTH` (a interface mostra `SHARE_TOO_LARGE_MESSAGE`).
 */
export function encodeProject(p: Project): string {
  const token = encodeProjectRaw(p);
  return token.length > MAX_SHARE_LENGTH ? '' : token;
}

/** Como `encodeProject`, mas sem o teto de tamanho. Util para diagnostico. */
export function encodeProjectRaw(p: Project): string {
  const json = JSON.stringify(packProject(p));
  const bytes = new TextEncoder().encode(json);
  return SHARE_TAG + toBase64Url(bytes);
}

/** Token do link -> projeto. `null` para token vazio, corrompido ou de outra versao. */
export function decodeProject(token: string): Project | null {
  if (typeof token !== 'string') return null;
  const clean = token.trim();
  if (clean.length < 2 || clean[0] !== SHARE_TAG) return null;

  const bytes = fromBase64Url(clean.slice(1));
  if (bytes === null || bytes.length === 0) return null;

  let json: string;
  try {
    json = new TextDecoder().decode(bytes);
  } catch {
    return null;
  }

  let packed: unknown;
  try {
    packed = JSON.parse(json);
  } catch {
    return null;
  }

  return unpackProject(packed);
}

// -----------------------------------------------------------------------------
// URL
// -----------------------------------------------------------------------------

/** Endereco do app sem hash nem query, base do link compartilhado. */
export function shareBaseUrl(): string {
  if (typeof location === 'undefined') return '';
  return `${location.origin}${location.pathname}`;
}

/** Monta o link completo. `''` quando o projeto nao cabe. */
export function buildShareUrl(p: Project): string {
  const token = encodeProject(p);
  if (token === '') return '';
  return `${shareBaseUrl()}#${SHARE_PARAM}=${token}`;
}

export interface ShareLink {
  /** Link pronto, ou `''` quando nao coube. */
  url: string;
  token: string;
  /** Tamanho do token em caracteres. */
  length: number;
  /** Mensagem pt-BR quando algo impediu a geracao. */
  error: string | null;
}

/**
 * Versao detalhada de `buildShareUrl`: alem do link, devolve o tamanho e a
 * mensagem de erro pronta para exibir.
 */
export function createShareLink(p: Project): ShareLink {
  const token = encodeProjectRaw(p);
  if (token.length > MAX_SHARE_LENGTH) {
    return {
      url: '',
      token: '',
      length: token.length,
      error: `${SHARE_TOO_LARGE_MESSAGE} (o link ficaria com ${formatCount(token.length)} caracteres).`,
    };
  }
  return {
    url: `${shareBaseUrl()}#${SHARE_PARAM}=${token}`,
    token,
    length: token.length,
    error: null,
  };
}

/** Extrai o token do hash de uma URL (ou do proprio hash). */
export function extractToken(hashOrUrl: string): string | null {
  const m = new RegExp(`[#&?]${SHARE_PARAM}=([A-Za-z0-9\\-_]+)`).exec(hashOrUrl ?? '');
  return m ? (m[1] as string) : null;
}

export interface SharedProjectRead {
  /** Projeto do link, ou `null` quando nao ha link ou ele nao pode ser lido. */
  project: Project | null;
  /**
   * Mensagem pt-BR quando HAVIA um token no endereco e ele falhou.
   * `null` quando nao havia link nenhum (situacao normal).
   */
  error: string | null;
}

/**
 * Le o projeto do `#p=` separando "nao havia link" de "o link esta quebrado".
 * Aceita um hash/URL explicito, util para testes.
 */
export function readSharedProjectFromUrl(hashOrUrl?: string): SharedProjectRead {
  const source =
    hashOrUrl ?? (typeof location === 'undefined' ? '' : (location.hash ?? ''));
  const token = extractToken(source);
  if (token === null) return { project: null, error: null };

  const project = decodeProject(token);
  if (project === null) return { project: null, error: SHARE_BROKEN_LINK_MESSAGE };
  return { project, error: null };
}

/**
 * Avisa o usuario que o link nao pode ser aberto.
 *
 * Primeiro dispara `SHARE_ERROR_EVENT`; se alguem chamar `preventDefault()`,
 * a interface assumiu o aviso e nada mais e feito. Sem ninguem escutando,
 * mostramos uma faixa dispensavel no topo — o que nao pode acontecer e o
 * usuario abrir o link e ver, em silencio, um projeto que nao e o dele.
 */
export function reportShareProblem(message: string): void {
  if (typeof window !== 'undefined' && typeof CustomEvent === 'function') {
    const handled = !window.dispatchEvent(
      new CustomEvent(SHARE_ERROR_EVENT, { detail: { message }, cancelable: true }),
    );
    if (handled) return;
  }
  showShareBanner(message);
}

/** Id do elemento da faixa, para nao empilhar avisos repetidos. */
export const SHARE_BANNER_ID = 'pedra-share-alert';

/** Tempo ate a faixa sumir sozinha. */
const SHARE_BANNER_MS = 14_000;

/**
 * Faixa de aviso no canto INFERIOR DIREITO: fora da barra lateral, fora do
 * botao CALCULAR e fora das abas de resultado, para nao tapar nada que o
 * usuario precise clicar. Sai no clique ou sozinha depois de alguns segundos.
 */
function showShareBanner(message: string): void {
  if (typeof document === 'undefined' || document.body === null) return;
  document.getElementById(SHARE_BANNER_ID)?.remove();

  const box = document.createElement('div');
  box.id = SHARE_BANNER_ID;
  box.setAttribute('role', 'alert');
  box.style.cssText = [
    'position:fixed',
    'right:16px',
    'bottom:16px',
    'left:auto',
    'top:auto',
    'z-index:9999',
    'display:flex',
    'gap:12px',
    'align-items:flex-start',
    'justify-content:space-between',
    'max-width:min(460px, calc(100vw - 32px))',
    'padding:12px 14px',
    'border-radius:8px',
    'border:1px solid #E0A800',
    'background:#FFF4CE',
    'color:#3B3000',
    'font:14px/1.45 system-ui,sans-serif',
    'box-shadow:0 6px 20px rgba(0,0,0,.18)',
  ].join(';');

  const text = document.createElement('span');
  text.textContent = message;

  const close = document.createElement('button');
  close.type = 'button';
  close.textContent = 'Fechar';
  close.setAttribute('aria-label', 'Fechar aviso');
  close.style.cssText =
    'flex:0 0 auto;cursor:pointer;border:1px solid #E0A800;background:transparent;' +
    'color:inherit;border-radius:6px;padding:4px 10px;font:inherit';
  close.addEventListener('click', () => box.remove());

  box.appendChild(text);
  box.appendChild(close);
  document.body.appendChild(box);

  if (typeof setTimeout === 'function') {
    setTimeout(() => {
      if (box.isConnected) box.remove();
    }, SHARE_BANNER_MS);
  }
}

/**
 * Le o projeto do `#p=` da barra de enderecos. `null` quando nao ha.
 * Link quebrado NAO falha em silencio: avisa e limpa o endereco, para que
 * recarregar a pagina abra o app normalmente.
 */
export function readProjectFromUrl(): Project | null {
  if (typeof location === 'undefined') return null;
  const { project, error } = readSharedProjectFromUrl(location.hash ?? '');
  if (error !== null) {
    reportShareProblem(error);
    clearUrlProject();
  }
  return project;
}

/**
 * Apaga o `#p=` da barra de enderecos sem recarregar a pagina, para que o
 * link nao seja recompartilhado desatualizado depois de editar o projeto.
 */
export function clearUrlProject(): void {
  if (typeof location === 'undefined') return;
  if (extractToken(location.hash ?? '') === null) return;
  try {
    if (typeof history !== 'undefined' && typeof history.replaceState === 'function') {
      history.replaceState(null, '', location.pathname + location.search);
      return;
    }
  } catch {
    // Alguns navegadores bloqueiam replaceState em about:blank — cai no plano B.
  }
  location.hash = '';
}
